import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { Button } from "@/design-system/Button";
import { useTranslation } from "@/translations";

import type { CreateTicketModalFormValues } from "../types";
import { CreateTicketModalStep } from "../types";

type CreateTicketModalBreadcrumbs = {
  onChangeStep: (step: CreateTicketModalStep) => void;
};

export const CreateTicketModalBreadcrumbs = ({ onChangeStep }: CreateTicketModalBreadcrumbs): React.ReactNode => {
  const form = useFormContext<CreateTicketModalFormValues>();
  const { t } = useTranslation();

  const currTree = useWatch({
    name: "tree",
    control: form.control,
  });

  const items = useMemo(() => {
    if (!currTree.location) {
      return [];
    }

    // Include every node in the path except for the leaf node
    const currItems = [
      {
        label:
          currTree.location.location === "inside"
            ? t("page.ticket-category-tree.location-type.inside")
            : t("page.ticket-category-tree.location-type.outside"),
        onSelect: () => {
          form.resetField("tree.location");
          form.setValue("tree.path", []);
          onChangeStep(CreateTicketModalStep.SELECT_TREE_LOCATION);
        },
      },
    ];

    for (const node of currTree.path) {
      if (node.type === "leaf" || node.type === "leafOther") {
        continue;
      }

      currItems.push({
        label: node.name,
        onSelect: () => {
          const targetNodeIndex = currTree.path.indexOf(node);
          const newPath = currTree.path.slice(0, targetNodeIndex);

          form.setValue("tree.path", newPath);

          if (newPath.length === 0) {
            onChangeStep(CreateTicketModalStep.SELECT_TREE_ROOT_NODE);
          } else {
            onChangeStep(CreateTicketModalStep.SELECT_TREE_NODE);
          }
        },
      });
    }

    return currItems;
  }, [currTree.location, currTree.path, t, form, onChangeStep]);

  return (
    <ol className="flex flex-wrap items-center gap-space-xs">
      {items.map((item, index) => (
        <li className="flex items-center gap-space-xs" key={item.label}>
          {index > 0 && <ChevronRightIcon tone="secondary" size={16} />}
          <Button onClick={item.onSelect} variant="ghostSecondary" size="sm">
            <span className="text-caption text-text-secondary">{item.label}</span>
          </Button>
        </li>
      ))}
    </ol>
  );
};
